import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Footer } from './Footer';

const remoteAssetsFaqs = [
  {
    question: "What is LoRaWAN and why is it used for remote asset monitoring?",
    answer: "LoRaWAN is a low-power wide-area radio protocol that transmits sensor data over 2–15km on a single battery charge lasting years. It is purpose-built for remote farms, water infrastructure, and off-grid assets where cellular coverage is unreliable and running power cables is impractical."
  },
  {
    question: "How long do remote IoT sensor batteries last?",
    answer: "Depending on transmission frequency and sensor type, LoRaWAN sensor batteries typically last 2–7 years without replacement. Yuccan Tech's modules can be configured with small solar panels for indefinite off-grid operation at sites with sufficient sunlight."
  },
  {
    question: "Do remote sensors need internet at the field location?",
    answer: "No. Sensors transmit wirelessly to a LoRaWAN gateway, which is the only device that needs internet — via cellular or satellite. The gateway can be kilometres from the sensors, so sensors themselves need no on-site power or connectivity infrastructure."
  },
  {
    question: "What types of remote assets and parameters can be monitored?",
    answer: "Environmental conditions (temperature, rainfall, soil moisture, UV), water tanks and irrigation flow, electric fence voltage, equipment vibration and runtime hours, security perimeter, and solar/battery system status. Any physical parameter measurable with a sensor can be monitored and reported to your cloud dashboard."
  }
];

const remoteAssetsFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": remoteAssetsFaqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
  }))
};

const remoteAssetsBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://yuccan.tech/" },
    { "@type": "ListItem", "position": 2, "name": "Remote Asset Monitoring", "item": "https://yuccan.tech/remote-assets" }
  ]
};

const RemoteAssetsPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    assetType: '',
    message: ''
  });

  const [transactionalConsent, setTransactionalConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [showPhoneHint, setShowPhoneHint] = useState(false);

  // Load reCAPTCHA script on component mount
  useEffect(() => {
    // Skip reCAPTCHA injection during react-snap / headless pre-rendering
    if (navigator.webdriver || window.location.hostname === 'localhost') return;
    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js?render=6Ld5QC0tAAAAACBa4DGyEe6SVLaGCVB8huTeuxFF";
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      window.grecaptcha.ready(() => {
        console.log('reCAPTCHA v3 loaded successfully');
      });
    };
    
    script.onerror = () => {
      console.error('Failed to load reCAPTCHA script');
    };
    
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const filtered = value.replace(/[^+\d]/g, '');
      const sanitized = filtered.charAt(0) === '+'
        ? '+' + filtered.slice(1).replace(/\+/g, '')
        : filtered.replace(/\+/g, '');
      if (sanitized.length > 0 && sanitized.charAt(0) !== '+') {
        setShowPhoneHint(true);
        setTimeout(() => setShowPhoneHint(false), 2500);
        return;
      }
      if (sanitized !== value) {
        setShowPhoneHint(true);
        setTimeout(() => setShowPhoneHint(false), 2500);
      }
      setFormData(prev => ({ ...prev, phone: sanitized }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Execute reCAPTCHA v3 if available
      let recaptchaToken = null;
      if (window.grecaptcha) {
        recaptchaToken = await new Promise((resolve) => {
          window.grecaptcha.ready(async () => {
            try {
              const token = await window.grecaptcha.execute('6Ld5QC0tAAAAACBa4DGyEe6SVLaGCVB8huTeuxFF', {action: 'submit'});
              resolve(token);
            } catch (error) {
              resolve(null);
            }
          });
        });
      }

      // Prepare form data for API (match main contact form structure)
      const apiFormData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone.trim(),
        transactionalConsent: formData.phone.trim() ? transactionalConsent : false,
        marketingConsent: formData.phone.trim() ? marketingConsent : false,
        message: `Company: ${formData.company}\nAsset Type: ${formData.assetType}\n\nMessage: ${formData.message}`,
        formStartTime: Date.now() - 10000, // Anti-spam timing
        honeypot: '', // Anti-spam field
        recaptchaToken // Add reCAPTCHA token
      };

      // Make API call to backend
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiFormData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setSubmitMessage('Thank you! We\'ll contact you within 24 hours to discuss your remote asset monitoring needs.');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          assetType: '',
          message: ''
        });
        setTransactionalConsent(false);
        setMarketingConsent(false);
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('Sorry, there was an error sending your message. Please try again or contact us directly on +64 9 886 4638.');
    }
    
    setIsSubmitting(false);
  };

  return (
    <div>
      <Helmet>
        <title>Remote IoT Asset Monitoring — LoRaWAN NZ | Yuccan Tech</title>
        <meta name="description" content="Low-power LoRaWAN IoT sensors for remote farms, water tanks, and infrastructure across New Zealand. Environmental, security & equipment monitoring. Solar/battery powered." />
        <link rel="canonical" href="https://yuccan.tech/remote-assets" />
        <meta property="og:title" content="Remote IoT Asset Monitoring — LoRaWAN NZ | Yuccan Tech" />
        <meta property="og:description" content="Monitor remote farms, water tanks, and infrastructure with low-power LoRaWAN sensors. Solar/battery powered. No on-site internet needed." />
        <meta property="og:url" content="https://yuccan.tech/remote-assets" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(remoteAssetsFaqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(remoteAssetsBreadcrumb)}</script>
      </Helmet>
    <div className="telematics-page remote-assets-page">
      {/* Logo Header */}
      <div className="telematics-header">
        <div className="container">
          <div className="logo-container">
            <a href="/">
              <img 
                src="/img/Banner_16x_TR_Trim&ColorEdit.png" 
                alt="Yuccan Technologies Logo" 
                className="telematics-logo"
              />
            </a>
          </div>
        </div>
      </div>
      {/* Hero Section */}
      <section className="telematics-hero">
        <video 
          autoPlay
          muted
          loop
          playsInline
          onPlay={() => {
            // Release wake lock to allow screen sleep
            if ('wakeLock' in navigator) {
              navigator.wakeLock.request('screen').then(wakeLock => {
                wakeLock.release();
              }).catch(() => {});
            }
          }}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            top: 0,
            left: 0,
            zIndex: -1
          }}
        >
          <source src="/vid/Remote.mp4" type="video/mp4" />
        </video>
        
        <div className="hero-overlay">
          <div className="container">
            <div className="row">
              <div className="col-md-6">
                <div className="hero-content-overlay">
                  <h1 className="hero-title">
                    Remote Asset Monitoring & Control.<br />
                    <span className="text-primary">Powered by LoRaWAN IoT.</span>
                  </h1>
                  <p className="hero-subtitle">
                    Deploy low-power IoT sensors and energy-efficient actuators at remote locations using 
                    LoRaWAN communications. Perfect for agricultural outstations, remote infrastructure, 
                    and distributed assets with solar/battery power systems, minimizing operational costs 
                    while maximizing monitoring coverage.
                  </p>
                  <div className="hero-stats">
                    <div className="stat-item">
                      <div className="stat-number">10+</div>
                      <div className="stat-label">Year Battery Life</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">15km</div>
                      <div className="stat-label">Typical Signal Range</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">IP67</div>
                      <div className="stat-label">Weather Protection</div>
                    </div>
                  </div>
                  <div className="hero-cta">
                    <a href="#features" className="btn btn-outline-primary btn-lg">Explore Solutions</a>
                    <a href="#contact" className="btn btn-primary btn-lg">Start Remote Monitoring</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile-only Hero Content Section */}
      <section className="mobile-hero-content">
        <div className="container">
          <div className="row">
            <div className="col-xs-12">
              <div className="mobile-hero-content-container">
                <h1 className="mobile-hero-title">
                  Remote Asset Monitoring & Control.<br />
                  <span className="text-primary">Powered by LoRaWAN IoT.</span>
                </h1>
                <p className="mobile-hero-subtitle">
                  Deploy low-power IoT sensors and energy-efficient actuators at remote locations using 
                  LoRaWAN communications. Perfect for agricultural outstations, remote infrastructure, 
                  and distributed assets with solar/battery power systems, minimizing operational costs 
                  while maximizing monitoring coverage.
                </p>
                <div className="mobile-hero-stats">
                  <div className="mobile-stat-item">
                    <div className="mobile-stat-number">10+</div>
                    <div className="mobile-stat-label">Year Battery Life</div>
                  </div>
                  <div className="mobile-stat-item">
                    <div className="mobile-stat-number">15km</div>
                    <div className="mobile-stat-label">Typical Signal Range</div>
                  </div>
                  <div className="mobile-stat-item">
                    <div className="mobile-stat-number">IP67</div>
                    <div className="mobile-stat-label">Weather Protection</div>
                  </div>
                </div>
                <div className="mobile-hero-cta">
                  <a href="#features" className="btn btn-outline-primary btn-lg">Explore Solutions</a>
                  <a href="#contact" className="btn btn-primary btn-lg">Start Remote Monitoring</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section id="benefits" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Low Power Monitoring & Control</h2>
            <p>Advanced LoRaWAN technology enables years of autonomous operation with minimal infrastructure requirements</p>
          </div>
          <div className="row">
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-battery-full"></i>
                <div className="service-desc">
                  <h3>Ultra-Low Power Modules</h3>
                  <p>Battery-powered sensors with 5-15 year operational life. Solar panel integration for continuous operation. Perfect for locations without mains power infrastructure.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-wifi"></i>
                <div className="service-desc">
                  <h3>Long-Range LoRaWAN Connectivity</h3>
                  <p>15km+ communication range in rural areas, 2-5km in urban environments. Minimal cellular network dependency, reducing ongoing operational costs significantly.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-cloud"></i>
                <div className="service-desc">
                  <h3>Cloud Integration & Analytics</h3>
                  <p>Real-time data visualization, automated alerting, predictive analytics, and integration with existing enterprise systems via REST APIs.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section id="features" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Comprehensive IoT Solutions</h2>
            <p>Complete sensor and actuator packages for every remote monitoring & control application</p>
          </div>
          <div className="row">
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-leaf"></i>
                <div className="service-desc">
                  <h3>Environmental Monitoring</h3>
                  <p>Temperature, humidity, soil moisture, rainfall, wind speed/direction, UV index, and atmospheric pressure monitoring for agricultural and weather applications.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-tint"></i>
                <div className="service-desc">
                  <h3>Water & Tank Monitoring</h3>
                  <p>Water level sensors, flow meters, pressure monitoring, and leak detection. Perfect for remote water tanks, irrigation systems, and water infrastructure.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-eye"></i>
                <div className="service-desc">
                  <h3>Security & Access Control</h3>
                  <p>Motion detection, door/gate sensors, camera triggers, and perimeter monitoring. Instant alerts for unauthorized access or security breaches.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-cogs"></i>
                <div className="service-desc">
                  <h3>Equipment Condition Monitoring</h3>
                  <p>Vibration sensors, current monitoring, runtime hours, maintenance scheduling, and predictive failure analysis for remote equipment and machinery.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-signal"></i>
                <div className="service-desc">
                  <h3>Wireless Sensor Networks</h3>
                  <p>Multi-hop mesh networks for distributed sensing across large areas. Self-healing network topology with automatic routing and data aggregation for comprehensive coverage.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="service-item">
                <i className="fa fa-toggle-on"></i>
                <div className="service-desc">
                  <h3>Remote Control & Automation</h3>
                  <p>Remote switching, valve control, pump activation, irrigation automation, and emergency shutdown capabilities via LoRaWAN commands.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Applications Section */}
      <section id="industries" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Perfect for Remote Applications</h2>
            <p>Proven solutions across diverse industries and use cases</p>
          </div>
          <div className="row">
            <div className="col-md-3 col-sm-6 col-xs-12">
              <div className="industry-card">
                <div className="industry-photo industry-agric&farming" style={{backgroundImage: 'url(/img/remote-assets/industry-agric&farming.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Agriculture & Farming</h3>
                  <p>Soil monitoring, livestock tracking, irrigation control, weather stations, and crop protection for improved yields and efficiency.</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 col-xs-12">
              <div className="industry-card">
                <div className="industry-photo industry-util&infra" style={{backgroundImage: 'url(/img/remote-assets/industry-util&infra.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Utilities & Infrastructure</h3>
                  <p>Remote meter reading, pipeline monitoring, power grid monitoring, and infrastructure health assessment for utility providers.</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 col-xs-12">
              <div className="industry-card">
                <div className="industry-photo industry-env-monitoring" style={{backgroundImage: 'url(/img/remote-assets/industry-env-monitoring.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Environmental Monitoring</h3>
                  <p>Air quality monitoring, water quality assessment, wildlife tracking, and environmental compliance reporting for research and regulation.</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 col-xs-12">
              <div className="industry-card">
                <div className="industry-photo industry-remote" style={{backgroundImage: 'url(/img/remote-assets/industry-remote.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Remote Facilities</h3>
                  <p>Site security, equipment monitoring, environmental control, and maintenance scheduling for remote buildings and facilities.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Showcase */}
      <section id="technology" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Enterprise-Grade LoRaWAN Technology</h2>
            <p>Professional IoT hardware and software designed for challenging environments</p>
          </div>
          <div className="row">
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="technology-card">
                <div className="technology-photo technology-equipment" style={{backgroundImage: 'url(/img/remote-assets/technology-equipment.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Rugged Outdoor Equipment</h3>
                  <p>Comprehensive web dashboard with customizable widgets, real-time alerts, historical data analysis, and automated reporting capabilities.</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="technology-card">
                <div className="technology-photo technology-mobile-config" style={{backgroundImage: 'url(/img/remote-assets/technology-mobile-config.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Mobile Management</h3>
                  <p>Native iOS and Android apps for field spot tests and configuration of devices and equipment via Bluetooth Low Energy (BLE).</p>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 col-xs-12">
              <div className="technology-card">
                <div className="technology-photo technology-sensors" style={{backgroundImage: 'url(/img/remote-assets/technology-sensors.jpg)'}}></div>
                <div className="service-desc">
                  <h3>Industrial-Grade Sensors</h3>
                  <p>IP67-rated weatherproof enclosures, -40°C to +85°C operating range, lightning protection, and tamper detection for harsh conditions.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="text-center" style={{paddingTop: '60px', paddingBottom: '60px', backgroundColor: '#f8f9fa'}}>
        <div className="container">
          <div className="section-title">
            <h2>Remote IoT Monitoring — Frequently Asked Questions</h2>
          </div>
          <div className="row">
            <div className="col-md-8 col-md-offset-2" style={{textAlign: 'left'}}>
              {remoteAssetsFaqs.map((faq, i) => (
                <div key={i} style={{borderBottom: '1px solid #dee2e6', marginBottom: '0'}}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      width: '100%', padding: '18px 0', background: 'none', border: 'none',
                      textAlign: 'left', fontSize: '16px', fontWeight: '600', color: '#333', cursor: 'pointer'
                    }}
                  >
                    <span>{faq.question}</span>
                    <span style={{fontSize: '20px', color: '#f8825a', marginLeft: '16px', flexShrink: 0}}>
                      {openFaq === i ? '−' : '+'}
                    </span>
                  </button>
                  {openFaq === i && (
                    <div style={{paddingBottom: '18px', color: '#555', lineHeight: '1.7', fontSize: '15px'}}>
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact/Get Started Section */}
      <section id="contact" className="text-center">
        <div className="container">
          <div className="section-title">
            <h2>Ready to Monitor Your Remote Assets?</h2>
            <p>Get started with a free assessment and discover the perfect IoT monitoring solution for your needs.</p>
          </div>
          <div className="row">
            <div className="col-md-8 col-md-offset-2">
                <form onSubmit={handleSubmit} className="process-contact-form">
                  <div className="form-group">
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      placeholder="Your Name *"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          placeholder="Email Address *"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="text"
                          name="company"
                          className="form-control"
                          placeholder="Company Name"
                          value={formData.company}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group" style={{position: 'relative'}}>
                        <input
                          type="tel"
                          name="phone"
                          className="form-control"
                          placeholder="Phone Number"
                          value={formData.phone}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                        {showPhoneHint && (
                          <div style={{
                            position: 'absolute', top: '-38px', left: '0', right: '0',
                            backgroundColor: '#333', color: '#fff', fontSize: '12px',
                            padding: '6px 10px', borderRadius: '4px', textAlign: 'center',
                            zIndex: 10, whiteSpace: 'nowrap'
                          }}>
                            Only + followed by digits allowed
                            <div style={{
                              position: 'absolute', bottom: '-6px', left: '50%', marginLeft: '-6px',
                              width: 0, height: 0, borderLeft: '6px solid transparent',
                              borderRight: '6px solid transparent', borderTop: '6px solid #333'
                            }} />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <select
                          name="assetType"
                          className="form-control"
                          value={formData.assetType}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        >
                          <option value="">Select Asset Type</option>
                          <option value="Agricultural Equipment">Agricultural Equipment</option>
                          <option value="Infrastructure & Utilities">Infrastructure & Utilities</option>
                          <option value="Environmental Monitoring">Environmental Monitoring</option>
                          <option value="Security & Access Control">Security & Access Control</option>
                          <option value="Water & Tank Management">Water & Tank Management</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <textarea
                      name="message"
                      className="form-control"
                      rows="4"
                      placeholder="Tell us about your remote monitoring requirements..."
                      value={formData.message}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    ></textarea>
                  </div>

                  {formData.phone.trim() && (
                    <>
                      <div style={{clear: 'both', overflow: 'hidden', marginTop: '-5px', marginBottom: '10px'}}>
                        <label style={{display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13px', color: '#fff', fontWeight: 'normal', float: 'none', width: '100%'}}>
                          <input
                            type="checkbox"
                            checked={transactionalConsent}
                            onChange={(e) => setTransactionalConsent(e.target.checked)}
                            disabled={isSubmitting}
                            style={{marginRight: '8px', marginTop: '2px', flexShrink: 0}}
                          />
                          <span style={{textAlign: 'justify'}}>
                            By checking this box, I consent to receive non-marketing text messages from Yuccan Technologies at the phone number provided. Messages may include responses to inquiries, appointment or consultation confirmations, service updates, and follow-up communications relevant to your request. Message frequency is twice or less per day; message and data rates may apply. Reply STOP to opt out.
                          </span>
                        </label>
                      </div>
                      <div style={{clear: 'both', overflow: 'hidden', marginBottom: '15px'}}>
                        <label style={{display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13px', color: '#fff', fontWeight: 'normal', float: 'none', width: '100%'}}>
                          <input
                            type="checkbox"
                            checked={marketingConsent}
                            onChange={(e) => setMarketingConsent(e.target.checked)}
                            disabled={isSubmitting}
                            style={{marginRight: '8px', marginTop: '2px', flexShrink: 0}}
                          />
                          <span style={{textAlign: 'justify'}}>
                            By checking this box, I consent to receive marketing and promotional text messages from Yuccan Technologies at the phone number provided. Messages may include special offers, discounts, and new product updates among others. Message frequency is twice or less per day; message and data rates may apply. Reply STOP to opt out.
                          </span>
                        </label>
                      </div>
                    </>
                  )}

                  {submitMessage && (
                    <div className={`alert ${submitStatus === 'success' ? 'alert-success' : 'alert-danger'}`}>
                      {submitMessage}
                    </div>
                  )}

                  <div className="privacy-notice" style={{margin: '15px 0', fontSize: '14px', color: '#fff'}}>
                    <p style={{fontSize: '16px', margin: 0}}>
                      By submitting this form, you agree to our{" "}
                      <a href="/legal/terms-of-use.html" target="_blank" rel="noopener noreferrer" style={{color: '#2B4B8C'}}>
                        Terms of Use
                      </a>
                      {" "}and{" "}
                      <a href="/legal/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{color: '#2B4B8C'}}>
                        Privacy Policy
                      </a>
                      .
                    </p>
                  </div>

                  <div className="text-center">
                    <button
                      type="submit"
                      className="btn btn-custom btn-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending Request...' : 'Get Free Assessment'}
                    </button>
                  </div>

                  <p className="form-note">
                    * No obligation. We'll provide a custom IoT monitoring solution analysis within 24 hours.
                  </p>
                </form>
            </div>
          </div>
        </div>
      </section>

    </div>
    
    {/* Universal Footer */}
    <Footer />
    </div>
  );
};

export default RemoteAssetsPage;